import { ApiError } from "../api/http_client.js";
import type {
  RoyalWheelPayloadDto,
  RoyalWheelRewardDto,
  RoyalWheelSectorDto,
  RoyalWheelSpinDto,
} from "../api/dto/royal_wheel.dto.js";
import {
  collectRoyalWheel,
  debugAddRoyalWheelTokens,
  debugResetRoyalWheel,
  getRoyalWheel,
  spinRoyalWheel,
} from "../api/requests/royal_wheel.requests.js";

const SIZE = 280;
const RADIUS = SIZE / 2 - 4;
const SECTOR_COLORS: Record<string, [string, string]> = {
  regular: ["#2c3e66", "#24345a"],
  special: ["#b7791f", "#a86d16"],
  jackpot: ["#7e3fbf", "#6f35a8"],
};
const TYPE_MARK: Record<string, string> = { regular: "", special: "★", jackpot: "💰" };
const STOP_EVENTS = ["pointerdown", "pointerup", "pointermove", "mousedown", "mouseup", "touchstart", "touchend", "touchmove", "click", "wheel"];

interface HitRow
{
  round: number;
  sector: number;
  type: string;
}

export class RoyalWheelDebug
{
  private static container: HTMLDivElement | null = null;
  private static wheel_svg: SVGSVGElement | null = null;
  private static info: HTMLDivElement | null = null;
  private static order_box: HTMLDivElement | null = null;
  private static log_box: HTMLDivElement | null = null;
  private static status: HTMLDivElement | null = null;
  private static state: RoyalWheelPayloadDto | null = null;
  private static rotation = 0;
  private static busy = false;
  private static stop_requested = false;
  private static hits: HitRow[] = [];
  private static log_lines: string[] = [];

  public static isVisible(): boolean
  {
    return this.container !== null;
  }

  public static toggle(): void
  {
    this.container ? this.close() : this.open();
  }

  public static open(): void
  {
    if ( !this.container )
      this.build();
    void this.refresh();
  }

  public static close(): void
  {
    this.stop_requested = true;
    this.container?.remove();
    this.container = null;
    this.wheel_svg = null;
    this.info = null;
    this.order_box = null;
    this.log_box = null;
    this.status = null;
  }

  public static getState(): RoyalWheelPayloadDto | null
  {
    return this.state;
  }

  public static getHits(): HitRow[]
  {
    return this.hits.slice();
  }

  public static async refresh(): Promise<void>
  {
    await this.run( "refresh", async () =>
    {
      this.state = await getRoyalWheel();
      this.rotation = 0;
      this.renderAll( false );
    } );
  }

  public static async spin( times = 1 ): Promise<void>
  {
    if ( this.busy )
      return;
    this.stop_requested = false;
    const duration = times > 1 ? 450 : 2200;
    for ( let i = 0; i < times && !this.stop_requested; i++ )
    {
      const ok = await this.run( "spin", async () =>
      {
        const response = await spinRoyalWheel( `dbg-${Date.now()}-${Math.random().toString( 36 ).slice( 2, 8 )}` );
        await this.animateTo( response.spin.sector_id, duration );
        const round_before = this.state?.wheel?.round ?? 0;
        this.recordSpin( round_before, response.spin );
        this.state = response;
        if ( response.spin.level_up )
          this.rotation = 0;
        this.renderAll( false );
      } );
      if ( !ok )
        break;
    }
  }

  public static async collect(): Promise<void>
  {
    await this.run( "collect", async () =>
    {
      const response = await collectRoyalWheel();
      this.pushLog( response.rewards.length ? `COLLECT ${this.rewardsText( response.rewards )}${response.jackpots ? ` (jackpots ×${response.jackpots})` : ""}` : "COLLECT: basket empty" );
      this.state = response;
      this.rotation = 0;
      this.renderAll( false );
    } );
  }

  public static async addTokens( amount: number ): Promise<void>
  {
    await this.run( "tokens", async () =>
    {
      const response = await debugAddRoyalWheelTokens( amount );
      this.pushLog( `TOKENS ${amount > 0 ? "+" : ""}${amount} → ${response.tokens}` );
      if ( this.state )
        this.state.tokens = response.tokens;
      this.renderInfo();
    } );
  }

  public static async resetState(): Promise<void>
  {
    if ( !confirm( "Drop the wheel state on the server? The basket is lost, the next fetch restarts the event." ) )
      return;
    await this.run( "reset", async () =>
    {
      await debugResetRoyalWheel();
      this.hits = [];
      this.pushLog( "RESET wheel state" );
      this.state = await getRoyalWheel();
      this.rotation = 0;
      this.renderAll( false );
    } );
  }

  private static async run( label: string, task: () => Promise<void> ): Promise<boolean>
  {
    if ( this.busy && label !== "spin" )
      return false;
    this.busy = true;
    this.setStatus( `${label}…`, "#94a3b8" );
    try
    {
      await task();
      this.setStatus( "", "#94a3b8" );
      return true;
    }
    catch ( error )
    {
      this.setStatus( this.errorText( error ), "#f87171" );
      return false;
    }
    finally
    {
      this.busy = false;
    }
  }

  private static errorText( error: unknown ): string
  {
    if ( error instanceof ApiError )
    {
      const body = error.body as { error?: string; tokens?: number } | undefined;
      if ( error.status === 404 && !body?.error )
        return "404: this server has no Royal Wheel routes (deploy it, or point the client at a local server)";
      return `${error.status} ${body?.error ?? error.message}${body?.tokens !== undefined ? ` (tokens ${body.tokens})` : ""}`;
    }
    return error instanceof Error ? error.message : String( error );
  }

  private static recordSpin( round: number, spin: RoyalWheelSpinDto ): void
  {
    this.hits.push( { round, sector: spin.sector_id, type: spin.sector_type } );
    const parts = [`L${round} ${TYPE_MARK[spin.sector_type]}#${spin.sector_id + 1}`];
    if ( spin.jackpot_rewards )
      parts.push( `JACKPOT ${this.rewardsText( spin.jackpot_rewards )}` );
    else
      parts.push( this.rewardsText( spin.sector_rewards ) );
    if ( spin.granted_tokens )
      parts.push( `+${spin.granted_tokens} token` );
    if ( spin.checkpoint_rewards )
      parts.push( `CHECKPOINT ${this.rewardsText( spin.checkpoint_rewards )}` );
    if ( spin.level_up )
      parts.push( `→ LEVEL ${round + 1}` );
    this.pushLog( parts.join( "  " ) );
  }

  private static pushLog( line: string ): void
  {
    this.log_lines.unshift( line );
    this.log_lines.length = Math.min( this.log_lines.length, 60 );
    if ( this.log_box )
      this.log_box.textContent = this.log_lines.join( "\n" );
  }

  private static rewardsText( rewards: RoyalWheelRewardDto[] ): string
  {
    return rewards.length ? rewards.map( reward => `${reward.type}×${reward.amount}` ).join( " " ) : "—";
  }

  private static setStatus( text: string, color: string ): void
  {
    if ( !this.status )
      return;
    this.status.textContent = text;
    this.status.style.color = color;
  }

  private static animateTo( sector_id: number, duration_ms: number ): Promise<void>
  {
    const svg = this.wheel_svg;
    const count = this.state?.wheel?.sectors.length ?? 0;
    if ( !svg || count === 0 )
      return Promise.resolve();
    const step = 360 / count;
    const target_mod = ( ( -( sector_id + 0.5 ) * step ) % 360 + 360 ) % 360;
    let delta = target_mod - ( ( this.rotation % 360 ) + 360 ) % 360;
    if ( delta < 0 )
      delta += 360;
    const turns = duration_ms > 1000 ? 4 : 1;
    this.rotation += delta + 360 * turns;
    svg.style.transition = `transform ${duration_ms}ms cubic-bezier(0.17, 0.67, 0.2, 1)`;
    svg.style.transform = `rotate(${this.rotation}deg)`;
    return new Promise( resolve => setTimeout( resolve, duration_ms + 60 ) );
  }

  private static build(): void
  {
    const wrap = document.createElement( "div" );
    wrap.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:2147483646",
      "display:flex",
      "align-items:flex-start",
      "justify-content:center",
      "padding-top:calc(env(safe-area-inset-top, 0px) + 8px)",
      "box-sizing:border-box",
      "background:rgba(0,0,0,0.55)",
      "font:12px/1.4 monospace",
      "color:#fff",
      "touch-action:none",
    ].join( ";" );
    for ( const name of STOP_EVENTS )
      wrap.addEventListener( name, event => event.stopPropagation() );

    const panel = document.createElement( "div" );
    panel.style.cssText = "width:min(380px,96vw);max-height:96vh;overflow:auto;background:#0f1424;border:1px solid rgba(255,255,255,0.18);border-radius:10px;padding:10px 12px;box-shadow:0 6px 30px rgba(0,0,0,0.7)";
    wrap.appendChild( panel );

    const header = document.createElement( "div" );
    header.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:6px";
    header.innerHTML = "<b style=\"color:#c4b5fd\">◆ ROYAL WHEEL (DEBUG)</b>";
    header.appendChild( this.button( "✕", () => this.close(), "#334155" ) );
    panel.appendChild( header );

    const info = document.createElement( "div" );
    info.style.cssText = "font-size:11px;color:#cbd5e1;margin-bottom:6px;white-space:pre-wrap;height:116px;overflow:auto";
    panel.appendChild( info );
    this.info = info;

    const box = document.createElement( "div" );
    box.style.cssText = `position:relative;width:${SIZE}px;height:${SIZE}px;margin:6px auto 4px`;
    const svg = document.createElementNS( "http://www.w3.org/2000/svg", "svg" );
    svg.setAttribute( "width", String( SIZE ) );
    svg.setAttribute( "height", String( SIZE ) );
    svg.setAttribute( "viewBox", `0 0 ${SIZE} ${SIZE}` );
    svg.style.cssText = "display:block;transform-origin:50% 50%";
    box.appendChild( svg );
    const pointer = document.createElement( "div" );
    pointer.style.cssText = "position:absolute;left:50%;top:-6px;transform:translateX(-50%);width:0;height:0;border-left:11px solid transparent;border-right:11px solid transparent;border-top:20px solid #f43f5e;filter:drop-shadow(0 1px 2px #000)";
    box.appendChild( pointer );
    panel.appendChild( box );
    this.wheel_svg = svg;

    const status = document.createElement( "div" );
    status.style.cssText = "height:30px;overflow:hidden;font-size:11px;text-align:center";
    panel.appendChild( status );
    this.status = status;

    const row1 = document.createElement( "div" );
    row1.style.cssText = "display:flex;gap:6px;flex-wrap:wrap;justify-content:center;margin:6px 0";
    row1.appendChild( this.button( "SPIN", () => void this.spin( 1 ), "#16a34a" ) );
    row1.appendChild( this.button( "SPIN ×10", () => void this.spin( 10 ), "#15803d" ) );
    row1.appendChild( this.button( "STOP", () => { this.stop_requested = true; }, "#475569" ) );
    row1.appendChild( this.button( "COLLECT", () => void this.collect(), "#2563eb" ) );
    panel.appendChild( row1 );

    const row2 = document.createElement( "div" );
    row2.style.cssText = "display:flex;gap:6px;flex-wrap:wrap;justify-content:center;margin-bottom:8px";
    row2.appendChild( this.button( "+10 TOKENS", () => void this.addTokens( 10 ), "#7c3aed" ) );
    row2.appendChild( this.button( "+100 TOKENS", () => void this.addTokens( 100 ), "#6d28d9" ) );
    row2.appendChild( this.button( "REFRESH", () => void this.refresh(), "#334155" ) );
    row2.appendChild( this.button( "RESET STATE", () => void this.resetState(), "#991b1b" ) );
    panel.appendChild( row2 );

    const order_title = document.createElement( "div" );
    order_title.style.cssText = "font-size:10px;color:#94a3b8;letter-spacing:.06em";
    order_title.textContent = "HIT ORDER PER LEVEL (this session)";
    panel.appendChild( order_title );
    const order_box = document.createElement( "div" );
    order_box.style.cssText = "font-size:11px;color:#e2e8f0;margin:2px 0 8px;white-space:pre-wrap";
    panel.appendChild( order_box );
    this.order_box = order_box;

    const log_title = document.createElement( "div" );
    log_title.style.cssText = "font-size:10px;color:#94a3b8;letter-spacing:.06em";
    log_title.textContent = "LOG";
    panel.appendChild( log_title );
    const log_box = document.createElement( "div" );
    log_box.style.cssText = "font-size:11px;color:#cbd5e1;white-space:pre;overflow:auto;max-height:150px;background:rgba(255,255,255,0.04);border-radius:6px;padding:4px 6px";
    log_box.textContent = this.log_lines.join( "\n" );
    panel.appendChild( log_box );
    this.log_box = log_box;

    document.documentElement.appendChild( wrap );
    this.container = wrap;
  }

  private static button( text: string, action: () => void, color: string ): HTMLButtonElement
  {
    const button = document.createElement( "button" );
    button.textContent = text;
    button.style.cssText = `font:bold 11px monospace;color:#fff;background:${color};border:0;border-radius:6px;padding:7px 10px;cursor:pointer`;
    button.addEventListener( "click", event =>
    {
      event.stopPropagation();
      action();
    } );
    return button;
  }

  private static renderAll( animate: boolean ): void
  {
    this.renderWheel( animate );
    this.renderInfo();
    this.renderOrder();
  }

  private static renderInfo(): void
  {
    if ( !this.info )
      return;
    const state = this.state;
    if ( !state )
    {
      this.info.textContent = "loading…";
      return;
    }
    const wheel = state.wheel;
    const lines = [`tokens: ${state.tokens}   catalog: ${state.enabled ? "enabled" : "DISABLED"}`];
    if ( !wheel )
    {
      lines.push( "no wheel: no live event for this player (admin → Royal Wheel: enabled, live dates, min level)" );
    }
    else
    {
      lines.push( `event ${wheel.id} · ${wheel.type} · ${wheel.phase.toUpperCase()} · ends ${wheel.ends_at.slice( 0, 16 ).replace( "T", " " )}` );
      lines.push( `level ${wheel.round} (main wheels: ${wheel.max_visible_round}) · open ${wheel.sectors.filter( sector => !sector.taken ).length}/${wheel.sectors.length}` );
      lines.push( `basket: ${this.rewardsText( wheel.basket )}${wheel.jackpots_in_basket ? ` · jackpots ×${wheel.jackpots_in_basket}` : ""}` );
      if ( wheel.jackpot )
        lines.push( `jackpot now: ${this.rewardsText( wheel.jackpot )}` );
      if ( wheel.checkpoint )
        lines.push( `${wheel.type === "jackpot" ? "jackpot growth" : "checkpoint"} ${wheel.checkpoint.index + 1}: ${wheel.checkpoint.progress}/${wheel.checkpoint.points_target} → ${this.rewardsText( wheel.checkpoint.rewards )}` );
      else if ( wheel.checkpoints_total )
        lines.push( `progress bar finished (${wheel.checkpoints_completed}/${wheel.checkpoints_total})` );
    }
    this.info.textContent = lines.join( "\n" );
  }

  private static renderOrder(): void
  {
    if ( !this.order_box )
      return;
    const by_round = new Map<number, HitRow[]>();
    for ( const hit of this.hits )
    {
      const list = by_round.get( hit.round ) ?? [];
      list.push( hit );
      by_round.set( hit.round, list );
    }
    const rounds = [...by_round.keys()].sort( ( a, b ) => b - a ).slice( 0, 6 );
    this.order_box.textContent = rounds.length
      ? rounds.map( round => `L${round}: ${by_round.get( round )!.map( hit => `${TYPE_MARK[hit.type]}#${hit.sector + 1}` ).join( " → " )}` ).join( "\n" )
      : "no spins yet";
  }

  private static renderWheel( animate: boolean ): void
  {
    const svg = this.wheel_svg;
    if ( !svg )
      return;
    const sectors = this.state?.wheel?.sectors ?? [];
    if ( !animate )
    {
      svg.style.transition = "none";
      svg.style.transform = `rotate(${this.rotation}deg)`;
    }
    const center = SIZE / 2;
    if ( !sectors.length )
    {
      svg.innerHTML = `<circle cx="${center}" cy="${center}" r="${RADIUS}" fill="#1e293b" stroke="#475569"/><text x="${center}" y="${center}" fill="#94a3b8" font-size="12" text-anchor="middle">no wheel</text>`;
      return;
    }
    const step = 360 / sectors.length;
    const parts: string[] = [];
    sectors.forEach( ( sector, index ) => parts.push( this.sectorSvg( sector, index, step, center ) ) );
    const ended = this.state?.wheel?.phase === "ended";
    parts.push( `<circle cx="${center}" cy="${center}" r="26" fill="#0f1424" stroke="#c4b5fd" stroke-width="2"/>` );
    parts.push( `<text x="${center}" y="${center + 5}" fill="#c4b5fd" font-size="14" font-weight="bold" text-anchor="middle">${ended ? "🔒" : `L${this.state?.wheel?.round ?? ""}`}</text>` );
    svg.innerHTML = parts.join( "" );
  }

  private static sectorSvg( sector: RoyalWheelSectorDto, index: number, step: number, center: number ): string
  {
    const start = ( index * step - 90 ) * Math.PI / 180;
    const end = ( ( index + 1 ) * step - 90 ) * Math.PI / 180;
    const mid = ( start + end ) / 2;
    const x1 = center + RADIUS * Math.cos( start );
    const y1 = center + RADIUS * Math.sin( start );
    const x2 = center + RADIUS * Math.cos( end );
    const y2 = center + RADIUS * Math.sin( end );
    const large = step > 180 ? 1 : 0;
    const colors = SECTOR_COLORS[sector.type] ?? SECTOR_COLORS.regular;
    const fill = sector.taken ? "#1a1f2e" : colors[index % 2];
    const label_r = RADIUS * 0.7;
    const lx = center + label_r * Math.cos( mid );
    const ly = center + label_r * Math.sin( mid );
    const radial = ( index + 0.5 ) * step;
    const rotate = radial > 90 && radial < 270 ? radial + 180 : radial;
    const reward = sector.rewards[0];
    const reward_text = sector.type === "jackpot" ? "JACKPOT" : reward ? `${this.shortType( reward.type )}${reward.amount}` : "";
    const text_color = sector.taken ? "#475569" : "#fff";
    return `<path d="M${center},${center} L${x1.toFixed( 2 )},${y1.toFixed( 2 )} A${RADIUS},${RADIUS} 0 ${large} 1 ${x2.toFixed( 2 )},${y2.toFixed( 2 )} Z" fill="${fill}" stroke="#0b0f1a" stroke-width="1.5"/>`
      + `<g transform="translate(${lx.toFixed( 2 )},${ly.toFixed( 2 )}) rotate(${rotate.toFixed( 2 )})">`
      + `<text y="-4" fill="${text_color}" font-size="11" font-weight="bold" text-anchor="middle">${TYPE_MARK[sector.type]}${index + 1}</text>`
      + `<text y="9" fill="${text_color}" font-size="9" text-anchor="middle"${sector.taken ? " text-decoration=\"line-through\"" : ""}>${reward_text}</text>`
      + "</g>";
  }

  private static shortType( type: string ): string
  {
    const map: Record<string, string> = {
      coin: "c", heart: "♥", infinite_hearts: "∞♥", no_ads: "noAd", fireworks: "fw", multiballs: "mb",
      big_balls: "bb", infinite_balls: "∞b", rockets: "rk", mines: "mn", wheel_tokens: "tk",
    };
    return map[type] ?? type.slice( 0, 3 );
  }
}
